import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchLocationOptions, fetchVendorOptions, fetchVendorRentalProductOptions } from '@/data/procurement';
import { fetchItemBasics, searchActiveInventorySkus } from '@/data/items';
import { getLocationTimezonesQueryOptions } from '@/data/locations';
import { createVehicleSubrental, type VehicleSubrentalLine } from '@/data/subrentalRecord';
import type { VendorEquipmentLine } from '@/data/vendorRentalInventory';
import { useEventsList } from '@/domain/events/useEvents';
import SegmentedToggle from '../form/SegmentedToggle';
import LocationDateTimeInput from '../form/LocationDateTimeInput';
import useVendorRentalWrite from '../../hooks/useVendorRentalWrite';
import { invalidateAvailabilityQueries } from '../../hooks/mutations/useInventoryMutations';
import { readableErrorMessage } from '../../utils/appErrors';
import SubrentalActionDialog from './SubrentalActionDialog';
import './NewSubrentalDialog.css';

export type NewSubrentalMode = 'equipment' | 'vehicle';

export type NewSubrentalDialogProps = {
  open: boolean;
  onClose: () => void;
  initialMode?: NewSubrentalMode;
  /** Prefills the first equipment line with this catalog item (ItemEditor and legacy links). */
  initialItemId?: string | null;
  /** The new record: the purchase order for equipment, the subrental for a vehicle with no order. */
  onCreated: (recordId: string) => void;
};

type RentalTerm = VendorEquipmentLine['rentalTerm'];
type TrackingMode = VendorEquipmentLine['trackingMode'];

type EquipmentLine = {
  id: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  itemSearch: string;
  productId: string;
  rentalTerm: RentalTerm;
  eventId: string;
  eventSearch: string;
  trackingMode: TrackingMode;
  qty: string;
  dailyCost: string;
  description: string;
  startAt: string;
  endAt: string;
  notes: string;
};

type VehicleLine = {
  id: string;
  vehicleName: string;
  vehicleType: string;
  licensePlate: string;
  capacity: string;
  qty: string;
  dailyCost: string;
  description: string;
  startAt: string;
  endAt: string;
  notes: string;
};

type OrderDraft = { locationId: string; vendorId: string; vendorName: string; notes: string };

const RENTAL_TERM_NOTES: { [term in RentalTerm]: string } = {
  event_specific: 'For one event: the units are held for that event at this site.',
  short_term_pool: 'Shared temporary stock: received units join this site’s stock only for the rental window.',
  long_term_pool: 'Long-term vendor stock: units stay in the SKU’s normal catalog subcategory, and usage, margin and ROI are tracked.',
};

const GUIDE = [
  'Pick the site the equipment comes to and the vendor it comes from. Every line is an existing catalog SKU.',
  'Confirmed incoming units help planning; picking needs them received.',
  'Track units one by one to keep each vendor tag (leading zeros included). Units without a usable tag get a Portal label.',
  'Return from an event puts units back in temporary stock. Return to vendor takes them out; units with events come back to the base first.',
];

function blankEquipmentLine(): EquipmentLine {
  return {
    id: crypto.randomUUID(),
    itemId: '',
    itemCode: '',
    itemName: '',
    itemSearch: '',
    productId: '',
    rentalTerm: 'short_term_pool',
    eventId: '',
    eventSearch: '',
    trackingMode: 'unit',
    qty: '1',
    dailyCost: '',
    description: '',
    startAt: '',
    endAt: '',
    notes: '',
  };
}

function blankVehicleLine(): VehicleLine {
  return {
    id: crypto.randomUUID(),
    vehicleName: '',
    vehicleType: '',
    licensePlate: '',
    capacity: '',
    qty: '1',
    dailyCost: '',
    description: '',
    startAt: '',
    endAt: '',
    notes: '',
  };
}

function wholeQuantity(value: string): number | null {
  const qty = Number(value);
  return Number.isSafeInteger(qty) && qty > 0 ? qty : null;
}

/** A blank cost stays unknown; anything else must be a number of zero or more. */
function dailyCostValue(value: string): { ok: boolean; cost: number | null } {
  const text = value.trim();
  if (!text) return { ok: true, cost: null };
  const cost = Number(text);
  return Number.isFinite(cost) && cost >= 0 ? { ok: true, cost } : { ok: false, cost: null };
}

function checkWindow(startAt: string, endAt: string, label: string): string | null {
  if (!startAt || !endAt) return `Choose when ${label} starts and when it goes back to the vendor.`;
  const start = Date.parse(startAt);
  const end = Date.parse(endAt);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return `Choose valid dates for ${label}.`;
  if (end <= start) return `${label.charAt(0).toUpperCase()}${label.slice(1)} must go back after it starts.`;
  if (end <= Date.now()) return `${label.charAt(0).toUpperCase()}${label.slice(1)} must go back in the future.`;
  return null;
}

/**
 * New subrental: equipment from a catalog vendor (by SKU, one purchase order) or temporary fleet
 * vehicles. Equipment goes through `create_vendor_equipment_rental` with a retry-safe request key
 * (useVendorRentalWrite); vehicles through the temporary-fleet workflow.
 */
export default function NewSubrentalDialog(props: NewSubrentalDialogProps) {
  if (!props.open) return null;
  return <NewSubrentalDialogBody {...props} />;
}

function NewSubrentalDialogBody({ onClose, initialMode, initialItemId, onCreated }: NewSubrentalDialogProps) {
  const queryClient = useQueryClient();
  const [mode, setMode] = React.useState<NewSubrentalMode>(initialMode ?? 'equipment');
  const [draft, setDraft] = React.useState<OrderDraft>({ locationId: '', vendorId: '', vendorName: '', notes: '' });
  const [equipmentLines, setEquipmentLines] = React.useState<EquipmentLine[]>(() => [blankEquipmentLine()]);
  const [vehicleLines, setVehicleLines] = React.useState<VehicleLine[]>(() => [blankVehicleLine()]);
  const [dirty, setDirty] = React.useState(false);
  const [confirmDiscard, setConfirmDiscard] = React.useState(false);
  const [vehicleBusy, setVehicleBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const mountedRef = React.useRef(true);
  React.useEffect(() => () => { mountedRef.current = false; }, []);
  const equipmentWrite = useVendorRentalWrite('create');

  const locationsQuery = useQuery({ queryKey: ['admin', 'location-options'], queryFn: () => fetchLocationOptions(), staleTime: 5 * 60_000 });
  const vendorsQuery = useQuery({ queryKey: ['warehouse', 'vendor-options'], queryFn: fetchVendorOptions, staleTime: 5 * 60_000 });
  const zonesQuery = useQuery(getLocationTimezonesQueryOptions({ locationIds: draft.locationId ? [draft.locationId] : [] }));
  const timezone = draft.locationId ? zonesQuery.data?.[draft.locationId] ?? null : null;

  const prefillQuery = useQuery({
    queryKey: ['items', 'basics', initialItemId ?? null],
    queryFn: async () => {
      const result = await fetchItemBasics({ itemId: String(initialItemId) });
      if (result.error) throw result.error;
      return result.data as { item_id?: string; item_code?: string | null; name_internal?: string | null } | null;
    },
    enabled: Boolean(initialItemId),
    staleTime: 60_000,
  });
  const prefilledRef = React.useRef(false);
  React.useEffect(() => {
    if (prefilledRef.current || !initialItemId || !prefillQuery.data) return;
    prefilledRef.current = true;
    const code = prefillQuery.data.item_code ?? '';
    const name = prefillQuery.data.name_internal ?? '';
    setEquipmentLines((current) => current.map((line, index) => (index === 0 && !line.itemId ? {
      ...line,
      itemId: initialItemId,
      itemCode: code,
      itemName: name,
      itemSearch: [code, name].filter(Boolean).join(' - '),
      description: name ? `${name} vendor rental` : code ? `${code} vendor rental` : '',
    } : line)));
  }, [initialItemId, prefillQuery.data]);

  const busy = vehicleBusy || equipmentWrite.busy;
  const equipmentPending = Boolean(equipmentWrite.pending);

  const markDirty = React.useCallback(function markDirty() {
    setDirty(true);
    setConfirmDiscard(false);
    setError(null);
  }, []);

  const updateDraft = React.useCallback(function updateDraft(patch: Partial<OrderDraft>) {
    setDraft((current) => ({ ...current, ...patch }));
    markDirty();
  }, [markDirty]);

  const updateEquipmentLine = React.useCallback(function updateEquipmentLine(lineId: string, patch: Partial<EquipmentLine>) {
    setEquipmentLines((current) => current.map((line) => (line.id === lineId ? { ...line, ...patch } : line)));
    markDirty();
  }, [markDirty]);

  const updateVehicleLine = React.useCallback(function updateVehicleLine(lineId: string, patch: Partial<VehicleLine>) {
    setVehicleLines((current) => current.map((line) => (line.id === lineId ? { ...line, ...patch } : line)));
    markDirty();
  }, [markDirty]);

  const removeEquipmentLine = React.useCallback(function removeEquipmentLine(lineId: string) {
    setEquipmentLines((current) => (current.length > 1 ? current.filter((line) => line.id !== lineId) : current));
    markDirty();
  }, [markDirty]);

  const removeVehicleLine = React.useCallback(function removeVehicleLine(lineId: string) {
    setVehicleLines((current) => (current.length > 1 ? current.filter((line) => line.id !== lineId) : current));
    markDirty();
  }, [markDirty]);

  function addLine() {
    if (mode === 'equipment') setEquipmentLines((current) => [...current, blankEquipmentLine()]);
    else setVehicleLines((current) => [...current, blankVehicleLine()]);
    markDirty();
  }

  function handleModeChange(equipment: boolean) {
    setMode(equipment ? 'equipment' : 'vehicle');
    setError(null);
  }

  function handleLocationChange(event: React.ChangeEvent<HTMLSelectElement>) {
    updateDraft({ locationId: event.target.value });
    // Event choices belong to a site.
    setEquipmentLines((current) => current.map((line) => (line.eventId ? { ...line, eventId: '', eventSearch: '' } : line)));
  }

  function handleVendorChange(event: React.ChangeEvent<HTMLSelectElement>) {
    updateDraft({ vendorId: event.target.value });
  }

  function handleVendorNameChange(event: React.ChangeEvent<HTMLInputElement>) {
    updateDraft({ vendorName: event.target.value });
  }

  function handleNotesChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    updateDraft({ notes: event.target.value });
  }

  function requestClose() {
    if (busy) return;
    if (dirty && !confirmDiscard) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  }

  function finishCreated(recordId: string | null) {
    void queryClient.invalidateQueries({ queryKey: ['warehouse'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'xrentals'] });
    invalidateAvailabilityQueries(queryClient);
    if (!recordId) {
      setError('The subrental was created, but its record could not be opened. Find it in the Subrentals list.');
      return;
    }
    onCreated(recordId);
  }

  function validateCommon(): string | null {
    if (!draft.locationId) return 'Choose the site the rental comes to.';
    if (!timezone) return zonesQuery.isError ? 'The site’s time zone could not be loaded. Choose the site again.' : 'Wait for the site’s time zone to load.';
    return null;
  }

  async function createEquipment() {
    const problem = validateCommon() ?? (!draft.vendorId ? 'Choose the vendor. Equipment rentals need a catalog vendor.' : null);
    if (problem) {
      setError(problem);
      return;
    }
    const lines: VendorEquipmentLine[] = [];
    for (const [index, line] of equipmentLines.entries()) {
      const label = `line ${index + 1}`;
      const qty = wholeQuantity(line.qty);
      const cost = dailyCostValue(line.dailyCost);
      const windowProblem = checkWindow(line.startAt, line.endAt, label);
      const lineProblem = !line.itemId ? `Choose an inventory SKU on ${label}.`
        : !qty ? `Enter a whole quantity of 1 or more on ${label}.`
          : !cost.ok ? `Enter a daily cost of 0 or more on ${label}, or leave it blank when unknown.`
            : windowProblem
              ?? (line.rentalTerm === 'long_term_pool' && !line.productId ? `Choose the catalog product on ${label}.` : null)
              ?? (line.rentalTerm === 'event_specific' && !line.eventId ? `Choose the event on ${label}.` : null);
      if (lineProblem) {
        setError(lineProblem);
        return;
      }
      const notes = line.notes.trim() || draft.notes.trim();
      lines.push({
        itemId: line.itemId,
        productId: line.productId || null,
        quantity: qty as number,
        description: line.description.trim() || `${line.itemCode || line.itemName || 'Equipment'} vendor rental`,
        startAt: new Date(line.startAt).toISOString(),
        endAt: new Date(line.endAt).toISOString(),
        dailyCost: cost.cost,
        rentalTerm: line.rentalTerm,
        trackingMode: line.trackingMode,
        ...(line.rentalTerm === 'event_specific' && line.eventId ? { eventId: line.eventId } : {}),
        ...(notes ? { notes } : {}),
      });
    }
    setError(null);
    const result = await equipmentWrite.run({
      kind: 'create',
      request: { requestKey: crypto.randomUUID(), locationId: draft.locationId, vendorId: draft.vendorId, notes: draft.notes.trim() || null, lines },
    });
    if (!mountedRef.current || !result || result.kind !== 'create') return;
    finishCreated(result.value.purchaseOrderId);
  }

  async function verifyPreviousCreation() {
    const result = await equipmentWrite.retry();
    if (!mountedRef.current || !result || result.kind !== 'create') return;
    finishCreated(result.value.purchaseOrderId);
  }

  async function createVehicles() {
    const problem = validateCommon();
    if (problem) {
      setError(problem);
      return;
    }
    const lines: VehicleSubrentalLine[] = [];
    for (const [index, line] of vehicleLines.entries()) {
      const label = `vehicle ${index + 1}`;
      const qty = wholeQuantity(line.qty);
      const cost = dailyCostValue(line.dailyCost);
      const lineProblem = !qty ? `Enter a whole quantity of 1 or more for ${label}.`
        : !cost.ok ? `Enter a daily cost of 0 or more for ${label}, or leave it blank when unknown.`
          : checkWindow(line.startAt, line.endAt, label);
      if (lineProblem) {
        setError(lineProblem);
        return;
      }
      lines.push({
        description: line.description.trim() || 'Temporary fleet vehicle rental',
        qty: qty as number,
        dailyCost: cost.cost,
        startAt: new Date(line.startAt).toISOString(),
        endAt: new Date(line.endAt).toISOString(),
        notes: line.notes.trim() || draft.notes.trim() || null,
        vehicleInfo: {
          name: line.vehicleName.trim() || line.description.trim() || 'Temporary fleet vehicle',
          vehicleType: line.vehicleType.trim() || null,
          licensePlate: line.licensePlate.trim() || null,
          capacity: line.capacity.trim() || null,
        },
      });
    }
    setError(null);
    setVehicleBusy(true);
    try {
      const created = await createVehicleSubrental({
        locationId: draft.locationId,
        vendorId: draft.vendorId || null,
        vendorName: draft.vendorName.trim() || null,
        notes: draft.notes.trim() || null,
        lines,
      });
      if (!mountedRef.current) return;
      finishCreated(created.purchaseOrderId ?? created.subrentalIds[0] ?? null);
    } catch (createError) {
      if (mountedRef.current) setError(readableErrorMessage(createError, 'The vehicle rental was not created. Check the details and try again.', 'mutation'));
    } finally {
      if (mountedRef.current) setVehicleBusy(false);
    }
  }

  function handleSubmit() {
    if (busy) return;
    if (mode === 'equipment') void createEquipment();
    else void createVehicles();
  }

  const vendors = (vendorsQuery.data ?? []).filter((vendor) => vendor.active !== false);
  const createLabel = mode === 'equipment' ? 'Create subrental' : 'Create vehicle rental';
  const shownError = error ?? (mode === 'equipment' ? equipmentWrite.error : null);
  const lines = mode === 'equipment' ? equipmentLines : vehicleLines;

  return (
    <SubrentalActionDialog
      open
      onClose={requestClose}
      eyebrow="New subrental"
      title="Rent from a vendor"
      subtitle="Equipment by catalog SKU, or temporary fleet vehicles. Incoming units help planning; picking needs them received."
      size="wide"
      busy={busy}
      error={shownError}
      errorTitle="The subrental was not created"
      onSubmit={handleSubmit}
      submitOnEnter={false}
      className="new-subrental-dialog"
      footerNote={confirmDiscard
        ? 'Discard this subrental? Press Close again to discard it, or keep editing.'
        : timezone ? `Times use ${timezone}.` : 'Choose a site to set rental times.'}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={requestClose} disabled={busy}>
            {confirmDiscard ? 'Discard' : 'Cancel'}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || (mode === 'equipment' && (equipmentPending || !equipmentWrite.ready))}
            title={mode === 'equipment' && equipmentPending ? 'Verify the earlier creation first.' : undefined}
          >
            {busy ? 'Creating…' : createLabel}
          </button>
        </>
      )}
    >
      <div className="new-subrental__mode">
        <SegmentedToggle
          value={mode === 'equipment'}
          onChange={handleModeChange}
          leftLabel="Equipment"
          rightLabel="Vehicle"
          size="sm"
          ariaLabel="What is rented"
        />
      </div>

      {mode === 'equipment' && equipmentPending ? (
        <div className="new-subrental__pending" role="alert">
          <p>An earlier subrental needs verifying before another one is created.</p>
          <button type="button" className="btn btn-secondary sm" disabled={equipmentWrite.busy} onClick={() => { void verifyPreviousCreation(); }}>
            Verify the earlier subrental
          </button>
        </div>
      ) : null}

      <fieldset className="new-subrental__fieldset" disabled={busy}>
        <legend className="sr-only">Rental order</legend>
        <div className="subrental-field-grid">
          <label className="subrental-field subrental-field--third">
            <span>Site</span>
            <select className="control" value={draft.locationId} onChange={handleLocationChange} aria-required="true">
              <option value="">{locationsQuery.isLoading ? 'Loading sites…' : 'Choose a site'}</option>
              {(locationsQuery.data ?? []).map((location) => (
                <option key={location.location_id} value={location.location_id}>{location.name}</option>
              ))}
            </select>
          </label>
          <label className="subrental-field subrental-field--third">
            <span>{mode === 'equipment' ? 'Vendor' : 'Vendor (optional)'}</span>
            <select className="control" value={draft.vendorId} onChange={handleVendorChange} aria-required={mode === 'equipment'}>
              <option value="">{vendorsQuery.isLoading ? 'Loading vendors…' : mode === 'equipment' ? 'Choose a vendor' : 'No linked vendor'}</option>
              {vendors.map((vendor) => (
                <option key={vendor.vendor_id} value={vendor.vendor_id}>{vendor.name}</option>
              ))}
            </select>
          </label>
          {mode === 'vehicle' ? (
            <label className="subrental-field subrental-field--third">
              <span>Vendor name</span>
              <input className="control" value={draft.vendorName} onChange={handleVendorNameChange} placeholder="When the vendor is not listed" />
            </label>
          ) : null}
          <label className="subrental-field subrental-field--full">
            <span>Notes</span>
            <textarea className="control" rows={2} maxLength={10000} value={draft.notes} onChange={handleNotesChange} />
          </label>
          {locationsQuery.isError || vendorsQuery.isError ? (
            <p className="subrental-field-note" role="alert">Sites or vendors could not be loaded. Close and open this dialog to try again.</p>
          ) : null}
        </div>

        <div className="new-subrental__lines-head">
          <h3>{mode === 'equipment' ? 'Equipment lines' : 'Vehicles'}</h3>
          <button type="button" className="btn btn-secondary sm" onClick={addLine}>
            {mode === 'equipment' ? 'Add line' : 'Add vehicle'}
          </button>
        </div>
        <ol className="new-subrental__lines">
          {mode === 'equipment'
            ? equipmentLines.map((line, index) => (
              <EquipmentLineFields
                key={line.id}
                line={line}
                index={index}
                locationId={draft.locationId}
                timezone={timezone}
                canRemove={lines.length > 1}
                onChange={updateEquipmentLine}
                onRemove={removeEquipmentLine}
              />
            ))
            : vehicleLines.map((line, index) => (
              <VehicleLineFields
                key={line.id}
                line={line}
                index={index}
                timezone={timezone}
                canRemove={lines.length > 1}
                onChange={updateVehicleLine}
                onRemove={removeVehicleLine}
              />
            ))}
        </ol>
      </fieldset>

      <details className="new-subrental__guide">
        <summary>How subrentals work</summary>
        <ul>
          {GUIDE.map((step) => <li key={step}>{step}</li>)}
        </ul>
      </details>
    </SubrentalActionDialog>
  );
}

type EquipmentLineFieldsProps = {
  line: EquipmentLine;
  index: number;
  locationId: string;
  timezone: string | null;
  canRemove: boolean;
  onChange: (lineId: string, patch: Partial<EquipmentLine>) => void;
  onRemove: (lineId: string) => void;
};

function EquipmentLineFields({ line, index, locationId, timezone, canRemove, onChange, onRemove }: EquipmentLineFieldsProps) {
  const label = `Line ${index + 1}`;
  const skuListId = React.useId();
  const eventListId = React.useId();
  const [skuResultsOpen, setSkuResultsOpen] = React.useState(false);
  const [eventResultsOpen, setEventResultsOpen] = React.useState(false);
  const skuTerm = line.itemSearch.trim();
  const skuQuery = useQuery({
    queryKey: ['vendor-rental', 'sku-search', skuTerm],
    queryFn: async () => {
      const result = await searchActiveInventorySkus({ term: skuTerm, limit: 8 });
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: skuTerm.length > 0 && !line.itemId,
    staleTime: 30_000,
  });
  const productQuery = useQuery({
    queryKey: ['vendor-rental', 'product-options', line.itemId],
    queryFn: () => fetchVendorRentalProductOptions(line.itemId),
    enabled: Boolean(line.itemId && line.rentalTerm === 'long_term_pool'),
    staleTime: 60_000,
  });
  const eventsQuery = useEventsList({
    filters: { search: line.eventSearch, siteLocationId: locationId, pageSize: 8, skipTotals: true },
    enabled: Boolean(locationId && line.rentalTerm === 'event_specific' && line.eventSearch.trim() && !line.eventId),
  });

  // Long-term stock sits in the SKU's normal catalog product: take the default one when it loads.
  React.useEffect(() => {
    if (line.rentalTerm !== 'long_term_pool' || line.productId || !productQuery.data?.length) return;
    const preferred = productQuery.data.find((option) => option.is_default) ?? productQuery.data[0];
    if (preferred) onChange(line.id, { productId: preferred.product_id });
  }, [line.id, line.productId, line.rentalTerm, onChange, productQuery.data]);

  const skuResults = !line.itemId && skuResultsOpen ? skuQuery.data ?? [] : [];
  const eventResults = !line.eventId && eventResultsOpen ? eventsQuery.data?.data ?? [] : [];

  function handleSkuSearch(event: React.ChangeEvent<HTMLInputElement>) {
    const itemSearch = event.target.value;
    setSkuResultsOpen(true);
    onChange(line.id, itemSearch === [line.itemCode, line.itemName].filter(Boolean).join(' - ') && line.itemId
      ? { itemSearch }
      : { itemSearch, itemId: '', itemCode: '', itemName: '', productId: '' });
  }

  function handleSearchKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Escape') return;
    if (event.currentTarget.getAttribute('aria-expanded') !== 'true') return;
    // The open result list takes Escape; the dialog stays open.
    event.preventDefault();
    event.stopPropagation();
    setSkuResultsOpen(false);
    setEventResultsOpen(false);
  }

  function chooseSku(event: React.MouseEvent<HTMLButtonElement>) {
    const itemId = event.currentTarget.dataset.itemId ?? '';
    const sku = (skuQuery.data ?? []).find((item) => item.item_id === itemId);
    if (!sku) return;
    setSkuResultsOpen(false);
    onChange(line.id, {
      itemId: sku.item_id,
      itemCode: sku.item_code,
      itemName: sku.name_internal,
      itemSearch: `${sku.item_code} - ${sku.name_internal}`,
      productId: '',
      description: line.description || `${sku.name_internal} vendor rental`,
    });
  }

  function handleRentalTermChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const value = event.target.value;
    const rentalTerm: RentalTerm = value === 'event_specific' || value === 'long_term_pool' ? value : 'short_term_pool';
    onChange(line.id, { rentalTerm, productId: '', eventId: '', eventSearch: '' });
  }

  function handleTrackingChange(event: React.ChangeEvent<HTMLSelectElement>) {
    onChange(line.id, { trackingMode: event.target.value === 'quantity' ? 'quantity' : 'unit' });
  }

  function handleEventSearch(event: React.ChangeEvent<HTMLInputElement>) {
    setEventResultsOpen(true);
    onChange(line.id, { eventSearch: event.target.value, eventId: '' });
  }

  function chooseEvent(event: React.MouseEvent<HTMLButtonElement>) {
    const eventId = event.currentTarget.dataset.eventId ?? '';
    const match = (eventsQuery.data?.data ?? []).find((row) => row.id === eventId);
    if (!match) return;
    setEventResultsOpen(false);
    onChange(line.id, { eventId: match.id, eventSearch: `${match.event_code || ''} — ${match.name || 'Event'}` });
  }

  function handleProductChange(event: React.ChangeEvent<HTMLSelectElement>) {
    onChange(line.id, { productId: event.target.value });
  }

  function handleField(event: React.ChangeEvent<HTMLInputElement>) {
    const field = event.currentTarget.dataset.field;
    if (field === 'qty' || field === 'dailyCost' || field === 'description' || field === 'notes') {
      const patch: Partial<EquipmentLine> = {};
      patch[field] = event.target.value;
      onChange(line.id, patch);
    }
  }

  function handleStart(startAt: string) {
    onChange(line.id, { startAt });
  }

  function handleEnd(endAt: string) {
    onChange(line.id, { endAt });
  }

  function handleRemove() {
    onRemove(line.id);
  }

  return (
    <li className="new-subrental__line" aria-label={label}>
      <div className="new-subrental__line-head">
        <strong>{label}</strong>
        <button type="button" className="btn btn-secondary xs" onClick={handleRemove} disabled={!canRemove} aria-label={`Remove ${label.toLowerCase()}`}>
          Remove
        </button>
      </div>
      <div className="subrental-field-grid">
        <label className="subrental-field new-subrental__search">
          <span>Inventory SKU</span>
          <input
            className="control"
            type="search"
            value={line.itemSearch}
            placeholder="Search item code or name"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={skuResults.length > 0}
            aria-controls={skuListId}
            onChange={handleSkuSearch}
            onKeyDown={handleSearchKeyDown}
          />
          {skuResults.length ? (
            <span id={skuListId} className="new-subrental__results" role="listbox" aria-label="Matching inventory SKUs">
              {skuResults.map((item) => (
                <button key={item.item_id} type="button" role="option" aria-selected="false" data-item-id={item.item_id} onClick={chooseSku}>
                  <strong>{item.item_code}</strong>
                  <span>{item.name_internal}</span>
                </button>
              ))}
            </span>
          ) : null}
        </label>
        <label className="subrental-field subrental-field--quarter">
          <span>Rental purpose</span>
          <select className="control" value={line.rentalTerm} onChange={handleRentalTermChange}>
            <option value="event_specific">For one event</option>
            <option value="short_term_pool">Shared temporary stock</option>
            <option value="long_term_pool">Long-term vendor stock</option>
          </select>
        </label>
        <label className="subrental-field subrental-field--quarter">
          <span>Track units</span>
          <select className="control" value={line.trackingMode} onChange={handleTrackingChange}>
            <option value="unit">Each unit individually</option>
            <option value="quantity">By quantity</option>
          </select>
        </label>
        <p className="subrental-field-note">
          {RENTAL_TERM_NOTES[line.rentalTerm]}
          {!line.itemId ? <> Missing SKU? <Link to="/admin/items">Create it in the catalog</Link>, then search for it here.</> : null}
          {skuQuery.isError ? ' The SKU search failed. Type again to retry.' : null}
        </p>

        {line.rentalTerm === 'event_specific' ? (
          <label className="subrental-field new-subrental__search">
            <span>Event at this site</span>
            <input
              className="control"
              type="search"
              value={line.eventSearch}
              disabled={!locationId}
              placeholder={locationId ? 'Search event code or name' : 'Choose the site first'}
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={eventResults.length > 0}
              aria-controls={eventListId}
              onChange={handleEventSearch}
              onKeyDown={handleSearchKeyDown}
            />
            {eventResults.length ? (
              <span id={eventListId} className="new-subrental__results" role="listbox" aria-label="Matching events">
                {eventResults.map((row) => (
                  <button key={row.id} type="button" role="option" aria-selected="false" data-event-id={row.id} onClick={chooseEvent}>
                    <strong>{row.event_code}</strong>
                    <span>{row.name}</span>
                  </button>
                ))}
              </span>
            ) : null}
          </label>
        ) : null}
        {line.rentalTerm === 'long_term_pool' ? (
          <label className="subrental-field">
            <span>Catalog product</span>
            <select className="control" value={line.productId} onChange={handleProductChange} disabled={!line.itemId || productQuery.isLoading}>
              <option value="">{line.itemId ? 'Choose an active product' : 'Choose the SKU first'}</option>
              {(productQuery.data ?? []).map((option) => (
                <option key={option.product_id} value={option.product_id}>
                  {option.product_code || option.name_public || option.product_id}
                  {option.name_public && option.product_code ? ` — ${option.name_public}` : ''}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {line.rentalTerm === 'event_specific' && eventsQuery.isError ? (
          <p className="subrental-field-note" role="alert">The event search failed. Type again to retry.</p>
        ) : null}
        {line.rentalTerm === 'long_term_pool' && line.itemId && !productQuery.isLoading && !productQuery.data?.length ? (
          <p className="subrental-field-note" role="alert">Link this SKU to an active catalog product before renting it as long-term vendor stock.</p>
        ) : null}

        <label className="subrental-field subrental-field--quarter">
          <span>Quantity</span>
          <input className="control" type="number" inputMode="numeric" min={1} step={1} value={line.qty} data-field="qty" onChange={handleField} />
        </label>
        <label className="subrental-field subrental-field--quarter">
          <span>Daily cost (per unit)</span>
          <input className="control" type="number" inputMode="decimal" min={0} step="0.01" value={line.dailyCost} placeholder="Unknown" data-field="dailyCost" onChange={handleField} />
        </label>
        {timezone ? (
          <>
            <div className="subrental-field-slot subrental-field-slot--quarter">
              <LocationDateTimeInput label="Rental starts" value={line.startAt} timezone={timezone} onChange={handleStart} />
            </div>
            <div className="subrental-field-slot subrental-field-slot--quarter">
              <LocationDateTimeInput label="Return to vendor by" value={line.endAt} timezone={timezone} onChange={handleEnd} />
            </div>
          </>
        ) : (
          <p className="subrental-field-note subrental-field-note--half">Choose a site to set the rental times.</p>
        )}

        <label className="subrental-field">
          <span>Description</span>
          <input className="control" value={line.description} placeholder={`${label} description`} data-field="description" onChange={handleField} />
        </label>
        <label className="subrental-field">
          <span>Line notes</span>
          <input className="control" value={line.notes} placeholder={`${label} notes`} data-field="notes" onChange={handleField} />
        </label>
      </div>
    </li>
  );
}

type VehicleLineFieldsProps = {
  line: VehicleLine;
  index: number;
  timezone: string | null;
  canRemove: boolean;
  onChange: (lineId: string, patch: Partial<VehicleLine>) => void;
  onRemove: (lineId: string) => void;
};

const VEHICLE_FIELDS = ['vehicleName', 'vehicleType', 'licensePlate', 'capacity', 'qty', 'dailyCost', 'description', 'notes'] as const;
type VehicleField = (typeof VEHICLE_FIELDS)[number];

function isVehicleField(value: string | undefined): value is VehicleField {
  return Boolean(value && (VEHICLE_FIELDS as readonly string[]).includes(value));
}

function VehicleLineFields({ line, index, timezone, canRemove, onChange, onRemove }: VehicleLineFieldsProps) {
  const label = `Vehicle ${index + 1}`;

  function handleField(event: React.ChangeEvent<HTMLInputElement>) {
    const field = event.currentTarget.dataset.field;
    if (!isVehicleField(field)) return;
    const patch: Partial<VehicleLine> = {};
    patch[field] = event.target.value;
    onChange(line.id, patch);
  }

  function handleStart(startAt: string) {
    onChange(line.id, { startAt });
  }

  function handleEnd(endAt: string) {
    onChange(line.id, { endAt });
  }

  function handleRemove() {
    onRemove(line.id);
  }

  return (
    <li className="new-subrental__line" aria-label={label}>
      <div className="new-subrental__line-head">
        <strong>{label}</strong>
        <button type="button" className="btn btn-secondary xs" onClick={handleRemove} disabled={!canRemove} aria-label={`Remove ${label.toLowerCase()}`}>
          Remove
        </button>
      </div>
      <div className="subrental-field-grid">
        <label className="subrental-field">
          <span>Vehicle name</span>
          <input className="control" value={line.vehicleName} placeholder="Cargo van" data-field="vehicleName" onChange={handleField} />
        </label>
        <label className="subrental-field subrental-field--quarter">
          <span>Type</span>
          <input className="control" value={line.vehicleType} data-field="vehicleType" onChange={handleField} />
        </label>
        <label className="subrental-field subrental-field--quarter">
          <span>Plate</span>
          <input className="control" value={line.licensePlate} data-field="licensePlate" onChange={handleField} />
        </label>

        <label className="subrental-field subrental-field--quarter">
          <span>Quantity</span>
          <input className="control" type="number" inputMode="numeric" min={1} step={1} value={line.qty} data-field="qty" onChange={handleField} />
        </label>
        <label className="subrental-field subrental-field--quarter">
          <span>Daily cost</span>
          <input className="control" type="number" inputMode="decimal" min={0} step="0.01" value={line.dailyCost} placeholder="Unknown" data-field="dailyCost" onChange={handleField} />
        </label>
        {timezone ? (
          <>
            <div className="subrental-field-slot subrental-field-slot--quarter">
              <LocationDateTimeInput label="Rental starts" value={line.startAt} timezone={timezone} onChange={handleStart} />
            </div>
            <div className="subrental-field-slot subrental-field-slot--quarter">
              <LocationDateTimeInput label="Return to vendor by" value={line.endAt} timezone={timezone} onChange={handleEnd} />
            </div>
          </>
        ) : (
          <p className="subrental-field-note subrental-field-note--half">Choose a site to set the rental times.</p>
        )}

        <label className="subrental-field subrental-field--quarter">
          <span>Capacity</span>
          <input className="control" value={line.capacity} data-field="capacity" onChange={handleField} />
        </label>
        <label className="subrental-field subrental-field--quarter">
          <span>Description</span>
          <input className="control" value={line.description} placeholder="Temporary fleet vehicle" data-field="description" onChange={handleField} />
        </label>
        <label className="subrental-field">
          <span>Notes</span>
          <input className="control" value={line.notes} data-field="notes" onChange={handleField} />
        </label>
      </div>
    </li>
  );
}
